import { onMounted, onUnmounted } from "vue";
import { Dropdown } from "bootstrap";

/**
 * Corrige le clipping des menus dropdown Bootstrap quand ils sont
 * placés dans un conteneur avec overflow (ex: .table-responsive).
 *
 * Au lieu de manipuler l'overflow du parent (ce qui bloquait le scroll
 * horizontal du tableau), on force Popper (utilisé en interne par
 * Bootstrap pour positionner le menu) à utiliser la stratégie "fixed".
 * Le menu sort alors du flux normal et n'est plus jamais coupé,
 * sans jamais toucher au style du tableau.
 *
 * Utilisation dans une page :
 *   import { useFixedDropdowns } from "@/utils/useFixedDropdowns";
 *   useFixedDropdowns();
 */
export function useFixedDropdowns(selector = '[data-bs-toggle="dropdown"]') {
  let instances: Dropdown[] = [];
  let observer: MutationObserver | null = null;

  const applyToElement = (el: Element) => {
    // Évite de ré-instancier un dropdown déjà configuré
    if ((el as any)._fixedDropdownApplied) return;
    (el as any)._fixedDropdownApplied = true;

    const instance = Dropdown.getOrCreateInstance(el, {
      popperConfig: (defaultConfig: any) => ({
        ...defaultConfig,
        strategy: "fixed",
      }),
    });
    instances.push(instance);
  };

  const applyAll = () => {
    document.querySelectorAll(selector).forEach(applyToElement);
  };

  onMounted(() => {
    applyAll();

    // Les lignes du tableau (utilisateurs, permissions révoquées, etc.)
    // sont générées dynamiquement après les appels API : on observe le DOM
    // pour appliquer le correctif aussi aux dropdowns ajoutés après coup.
    observer = new MutationObserver(() => applyAll());
    observer.observe(document.body, { childList: true, subtree: true });
  });

  onUnmounted(() => {
    // Nettoyage complet : plus aucun listener/instance ne survit
    // à la destruction du composant (évite l'accumulation au fil
    // des navigations qui causait le comportement "tantôt ça marche").
    observer?.disconnect();
    instances.forEach((i) => i.dispose());
    instances = [];
  });
}